import React from 'react';

export default function PlacementHeatmap({ nodePositions, emptySlots, treeData, isVisible }) {
  if (!isVisible) return null;

  // Get color based on available slots at node
  const getPlacementColor = (nodeId) => {
    let filledLegs = 0;
    const children = treeData.getChildren(nodeId);
    const filledLegSet = new Set(
      children.map((cId) => treeData.getNode(cId)?.leg_position).filter(Boolean)
    );
    filledLegs = filledLegSet.size;

    // Green = fully filled (3/3 legs)
    if (filledLegs === 3) return 'rgb(34, 197, 94)';
    // Yellow = partially filled (2/3 legs)
    if (filledLegs === 2) return 'rgb(234, 179, 8)';
    // Orange = mostly empty (1/3 leg)
    if (filledLegs === 1) return 'rgb(249, 115, 22)';
    // Red = completely empty (0/3 legs)
    return 'rgb(239, 68, 68)';
  };

  const getDepthColor = (depth, maxDepth) => {
    const ratio = maxDepth > 0 ? depth / maxDepth : 0;
    if (ratio < 0.33) {
      return `rgb(${Math.floor(ratio / 0.33 * 255)}, 100, 255)`;
    } else if (ratio < 0.66) {
      const progress = (ratio - 0.33) / 0.33;
      return `rgb(255, ${Math.floor(100 + progress * 155)}, ${Math.floor(255 - progress * 255)})`;
    } else {
      const progress = (ratio - 0.66) / 0.34;
      return `rgb(255, ${Math.floor(255 - progress * 155)}, 0)`;
    }
  };

  let maxDepth = 0;
  nodePositions.forEach((pos, nodeId) => {
    const node = treeData.getNode(nodeId);
    if (node && node.depth > maxDepth) maxDepth = node.depth;
  });

  return (
    <>
      {/* Legend */}
      <div className="absolute top-4 left-4 bg-surface border border-outline rounded-lg shadow-lg p-3 z-40">
        <div className="text-body-xs font-medium text-on-background mb-2">Placement Heatmap</div>
        
        <div className="mb-2">
          <div className="text-[10px] font-medium text-on-background/70 mb-1">Availability</div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(34, 197, 94)' }}></div>
            <span className="text-[10px] text-on-background/60">Full (3/3)</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(234, 179, 8)' }}></div>
            <span className="text-[10px] text-on-background/60">Partial (2/3)</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(249, 115, 22)' }}></div>
            <span className="text-[10px] text-on-background/60">Low (1/3)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgb(239, 68, 68)' }}></div>
            <span className="text-[10px] text-on-background/60">Empty (0/3)</span>
          </div>
        </div>
      </div>

      {/* Placement Glow (filled nodes) */}
      <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%">
        <defs>
          <filter id="placement-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Depth heatmap (background) */}
        {Array.from(nodePositions.entries()).map(([nodeId, pos]) => {
          const node = treeData.getNode(nodeId);
          if (!node) return null;

          const depthColor = getDepthColor(node.depth, maxDepth);
          return (
            <circle
              key={`depth-${nodeId}`}
              cx={pos.x}
              cy={pos.y}
              r="35"
              fill={depthColor}
              opacity="0.1"
              filter="url(#placement-glow)"
            />
          );
        })}

        {/* Placement heatmap (foreground, brighter) */}
        {Array.from(nodePositions.entries()).map(([nodeId, pos]) => {
          const placementColor = getPlacementColor(nodeId);
          return (
            <circle
              key={`placement-${nodeId}`}
              cx={pos.x}
              cy={pos.y}
              r="28"
              fill={placementColor}
              opacity="0.12"
              filter="url(#placement-glow)"
            />
          );
        })}

        {/* Empty slot indicators (smaller rings) */}
        {Array.from(emptySlots.entries()).map(([key, slot]) => {
          return (
            <circle
              key={`empty-${key}`}
              cx={slot.x}
              cy={slot.y}
              r="20"
              fill="rgb(107, 114, 128)"
              opacity="0.15"
              filter="url(#placement-glow)"
            />
          );
        })}
      </svg>
    </>
  );
}
